import JSZip from "jszip";
import { getBytes, getMetadata, ref as storageRef } from "firebase/storage";
import { storage } from "src/lib/firebase_config";
import { normalizeHappeningNowPlatforms } from "src/lib/happeningNowPlatforms";
import type { CarouselCardSnapshot } from "src/types/carouselPublication";
import type { CarouselPublicationListItem } from "src/types/carouselPublication";

const IMAGE_FETCH_TIMEOUT_MS = 30_000;

/** Races a promise against a deadline. Rejects with a timeout error if the deadline fires first. */
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label = "request",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(`Timed out after ${ms}ms (${label})`)),
      ms,
    );
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

export class CarouselPublicationExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CarouselPublicationExportError";
  }
}

function publicationFolderName(pub: CarouselPublicationListItem): string {
  return `pub-${pub.publishedAt.toDate().toISOString()}`;
}

function serializePublication(pub: CarouselPublicationListItem) {
  const cards = [...pub.cards].sort((a, b) => a.order - b.order);
  return {
    id: pub.id,
    publishedAt: pub.publishedAt.toDate().toISOString(),
    publishedBy: pub.publishedBy,
    publishedByDisplayName: pub.publishedByDisplayName,
    cardCount: pub.cardCount,
    starredAt: pub.starredAt?.toDate().toISOString() ?? null,
    starredBy: pub.starredBy ?? null,
    starredByDisplayName: pub.starredByDisplayName ?? null,
    imagePathsUsed: pub.imagePathsUsed,
    cards: cards.map((c) => ({
      id: c.id,
      title: c.title,
      description: c.description ?? null,
      imageUrl: c.imageUrl,
      imagePath: c.imagePath,
      imageWidth: c.imageWidth ?? null,
      imageHeight: c.imageHeight ?? null,
      targetLink: c.targetLink,
      startsAt: c.startsAt?.toDate().toISOString() ?? null,
      expiresAt: c.expiresAt.toDate().toISOString(),
      active: c.active,
      order: c.order,
      platforms: normalizeHappeningNowPlatforms(c.platforms),
      createdBy: c.createdBy,
      createdByDisplayName: c.createdByDisplayName,
    })),
  };
}

function extensionFromStoragePath(path: string): string | null {
  const leaf = path.split("/").pop() ?? path;
  const m = leaf.match(/\.(jpe?g|png|gif|webp)$/i);
  if (!m) return null;
  const e = m[1].toLowerCase();
  return e === "jpeg" ? "jpg" : e;
}

function extensionFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\.(jpe?g|png|gif|webp)$/i);
    if (!m) return null;
    const e = m[1].toLowerCase();
    return e === "jpeg" ? "jpg" : e;
  } catch {
    return null;
  }
}

function extensionFromContentType(ct: string): string | null {
  const base = ct.split(";")[0]?.trim().toLowerCase() ?? "";
  if (base.includes("jpeg")) return "jpg";
  if (base === "image/jpg") return "jpg";
  if (base.includes("png")) return "png";
  if (base.includes("gif")) return "gif";
  if (base.includes("webp")) return "webp";
  return null;
}

/** Fetches image bytes from Firebase Storage REST API using the caller's Firebase ID token.
 *  This is the primary strategy: the Authorization header works from any origin once CORS
 *  is configured on the bucket (storage.cors.json → gsutil cors set). */
async function fetchCoverWithIdToken(
  imagePath: string,
  idToken: string,
): Promise<{ ext: string; bytes: Uint8Array }> {
  const trimmed = imagePath.trim();
  if (!trimmed) throw new Error("Empty imagePath");

  const bucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;
  if (!bucket) throw new Error("REACT_APP_FIREBASE_STORAGE_BUCKET is not set");

  const encodedPath = encodeURIComponent(trimmed);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;

  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    IMAGE_FETCH_TIMEOUT_MS,
  );
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${idToken}` },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} from Storage REST API`);
    const bytes = new Uint8Array(await res.arrayBuffer());
    const ext =
      extensionFromContentType(res.headers.get("content-type") ?? "") ??
      extensionFromStoragePath(trimmed) ??
      "jpg";
    return { ext, bytes };
  } finally {
    clearTimeout(timer);
  }
}

/** Fallback: Firebase Storage SDK getBytes — works when Firebase Hosting is the origin or
 *  when the SDK's internal auth token satisfies CORS (same-project Hosting domains). */
async function fetchCoverFromStorage(
  imagePath: string,
): Promise<{ ext: string; bytes: Uint8Array }> {
  const trimmed = imagePath.trim();
  if (!trimmed) throw new Error("Empty imagePath");
  const ref = storageRef(storage, trimmed);
  let ext = extensionFromStoragePath(trimmed);
  if (!ext) {
    try {
      const meta = await getMetadata(ref);
      ext =
        (meta.contentType && extensionFromContentType(meta.contentType)) ??
        "jpg";
    } catch {
      ext = "jpg";
    }
  }
  const buf = await withTimeout(
    getBytes(ref),
    IMAGE_FETCH_TIMEOUT_MS,
    `storage:${trimmed}`,
  );
  return { ext, bytes: new Uint8Array(buf) };
}

/** Last resort: fetch the raw download URL (token-signed public URL). Fails when the
 *  bucket has no CORS configuration that covers the current origin. */
async function fetchCoverFromDownloadUrl(
  url: string,
): Promise<{ ext: string; bytes: Uint8Array }> {
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    IMAGE_FETCH_TIMEOUT_MS,
  );
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const bytes = new Uint8Array(await res.arrayBuffer());
    const fromMime = extensionFromContentType(
      res.headers.get("content-type") ?? "",
    );
    const fromPath = extensionFromUrl(url);
    const ext = fromMime ?? fromPath ?? "jpg";
    return { ext, bytes };
  } finally {
    clearTimeout(timer);
  }
}

async function loadCoverForExport(
  card: CarouselCardSnapshot,
  idToken: string | null,
): Promise<{
  ext: string;
  bytes: Uint8Array;
  source: "idToken" | "storage" | "downloadUrl";
}> {
  const path = card.imagePath?.trim();

  // Strategy 1: Firebase REST API + ID token (works cross-origin after CORS config)
  if (idToken && path) {
    try {
      const r = await fetchCoverWithIdToken(path, idToken);
      return { ...r, source: "idToken" };
    } catch {
      // fall through
    }
  }

  // Strategy 2: Firebase Storage SDK (works on Firebase Hosting origins)
  if (path) {
    try {
      const r = await fetchCoverFromStorage(path);
      return { ...r, source: "storage" };
    } catch {
      // fall through
    }
  }

  // Strategy 3: raw download URL with token
  const r = await fetchCoverFromDownloadUrl(card.imageUrl);
  return { ...r, source: "downloadUrl" };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Builds a zip: one folder per publication (`pub-{id}`), each with
 * `publication.txt` (JSON snapshot + export metadata) and `cover1.*`, `cover2.*`, …
 * in carousel order.
 *
 * Image fetch order:
 *  1. Firebase Storage REST API with caller's ID token (fastest, works cross-origin
 *     once `storage.cors.json` is applied via `gsutil cors set`).
 *  2. Firebase Storage SDK `getBytes` (works on Firebase Hosting).
 *  3. Download URL fetch (public token, same CORS requirement as #1).
 *
 * If all three fail the cover entry is recorded in exportMeta with an error and
 * the zip is still built without that image.
 */
export async function buildCarouselPublicationsZipBlob(
  publications: CarouselPublicationListItem[],
  idToken: string | null = null,
): Promise<Blob> {
  if (publications.length === 0) {
    throw new CarouselPublicationExportError(
      "No publications to export — history is empty.",
    );
  }

  const zip = new JSZip();
  const exportedAt = new Date().toISOString();

  for (const pub of publications) {
    const folder = zip.folder(publicationFolderName(pub));
    if (!folder) continue;

    const sortedCards = [...pub.cards].sort((a, b) => a.order - b.order);

    const coverMeta = await Promise.all(
      sortedCards.map(async (card, i) => {
        const index = i + 1;
        try {
          const { ext, bytes, source } = await loadCoverForExport(
            card,
            idToken,
          );
          const fileName = `cover${index}.${ext}`;
          folder.file(fileName, bytes);
          return {
            index,
            file: fileName,
            cardId: card.id,
            source,
            ok: true as const,
          };
        } catch (e) {
          return {
            index,
            file: null as string | null,
            cardId: card.id,
            ok: false as const,
            error: e instanceof Error ? e.message : String(e),
          };
        }
      }),
    );

    const payload = {
      exportMeta: {
        exportedAt,
        covers: coverMeta,
      },
      publication: serializePublication(pub),
    };

    folder.file("publication.txt", JSON.stringify(payload, null, 2));
  }

  return zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}
